import {
  MdLocationOn,
  MdHome,
  MdApartment,
  MdMap,
  MdPublic
} from "react-icons/md";
import { FormData, SetFormData } from './FormData.tsx';
import { useEffect, useState, useRef } from 'react';
import { AddressComponents } from '../../../components/AddressComponents';

interface AddressForm {
  formData: FormData;
  setFormData: SetFormData;
  setNextDisabled: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function WorkplaceLocation({ formData, setFormData, setNextDisabled }: AddressForm) {
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const addressInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const validateField = (field: keyof FormData, value: string): boolean => {
    let isValid = true;
    const newErrors = {...fieldErrors};

    switch(field) {
      case 'address':
        // Allows letters, numbers, spaces, and basic punctuation
        isValid = /^[a-zA-Z0-9\s\-/.,#]+$/.test(value);
        if (!isValid) newErrors.street = 'Invalid street address';
        else delete newErrors.street;
        break;
      case 'aptUnit':
        // Optional field, but if filled should be valid
        if (value && !/^[a-zA-Z0-9\s\-/#]+$/.test(value)) {
          isValid = false;
          newErrors.aptUnit = 'Invalid unit format';
        } else {
          delete newErrors.aptUnit;
        }
        break;
      case 'floor':
        // Optional field, but if filled should be valid
        if (value && !/^[a-zA-Z0-9\s-]+$/.test(value)) {
          isValid = false;
          newErrors.floor = 'Invalid floor format';
        } else {
          delete newErrors.floor;
        }
        break;
      case 'city':
        // Allows letters, spaces, and hyphens
        isValid = /^[a-zA-Z\s-]+$/.test(value);
        if (!isValid) newErrors.city = 'City can only contain letters and hyphens';
        else delete newErrors.city;
        break;
      case 'state':
        // Allows letters and spaces
        isValid = /^[a-zA-Z\s]+$/.test(value);
        if (!isValid) newErrors.state = 'State can only contain letters';
        else delete newErrors.state;
        break;
      case 'postalCode':
        // Allows numbers and optional hyphen (for countries with extended zip codes)
        isValid = /^[0-9-]+$/.test(value);
        if (!isValid) newErrors.postalCode = 'Postal code can only contain numbers and hyphens';
        else delete newErrors.postalCode;
        break;
      case 'country':
        // Allows letters and spaces
        isValid = /^[a-zA-Z\s]+$/.test(value);
        if (!isValid) newErrors.country = 'Country can only contain letters';
        else delete newErrors.country;
        break;
      default:
        delete newErrors[field];
    }

    setFieldErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    validateField(field, value);
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddressAutofill = (address: AddressComponents) => {
    // Update all address fields from autocomplete
    const updates: Partial<FormData> = {};
    const newErrors = {...fieldErrors};

    if (address.street) {
      updates.address = address.street;
      if (validateField('address', address.street)) {
        delete newErrors.street;
      }
    }
    if (address.city) {
      updates.city = address.city;
      if (validateField('city', address.city)) {
        delete newErrors.city;
      }
    }
    if (address.state) {
      updates.state = address.state;
      if (validateField('state', address.state)) {
        delete newErrors.state;
      }
    }
    if (address.postalCode) {
      updates.postalCode = address.postalCode;
      if (validateField('postalCode', address.postalCode)) {
        delete newErrors.postalCode;
      }
    }
    if (address.country) {
      updates.country = address.country;
      if (validateField('country', address.country)) {
        delete newErrors.country;
      }
    }

    setFieldErrors(newErrors);
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
  };

  // Sync input value with formData changes (from autocomplete)
  useEffect(() => {
    if (addressInputRef.current && formData.address) {
      addressInputRef.current.value = formData.address;
    }
  }, [formData.address]);

  useEffect(() => {
    // Initialize Google Places Autocomplete
    const initializeAutocomplete = () => {
      if (
        addressInputRef.current &&
        !autocompleteRef.current &&
        window.google &&
        window.google.maps &&
        window.google.maps.places
      ) {
        try {
          autocompleteRef.current = new google.maps.places.Autocomplete(
            addressInputRef.current,
            {
              types: ['address'],
              componentRestrictions: { country: 'au' },
              fields: [
                'address_components',
                'formatted_address',
                'geometry.location',
              ],
            }
          );

          autocompleteRef.current.addListener('place_changed', () => {
            const place = autocompleteRef.current?.getPlace();

            if (place && place.address_components) {
              const address: AddressComponents = {
                fullAddress: place.formatted_address,
              };

              // Parse address components
              let streetNumber = '';
              let route = '';
              let neighborhood = '';
              let sublocality = '';

              place.address_components.forEach((component) => {
                const types = component.types;

                if (types.includes('street_number')) {
                  streetNumber = component.long_name;
                }
                if (types.includes('route')) {
                  route = component.long_name;
                }
                if (types.includes('locality')) {
                  address.city = component.long_name;
                }
                // Fallback for city if locality not found
                if (types.includes('neighborhood')) {
                  neighborhood = component.long_name;
                }
                if (types.includes('sublocality') || types.includes('sublocality_level_1')) {
                  sublocality = component.long_name;
                }
                if (types.includes('administrative_area_level_1')) {
                  address.state = component.long_name;
                }
                if (types.includes('country')) {
                  address.country = component.long_name;
                }
                if (types.includes('postal_code')) {
                  address.postalCode = component.long_name;
                }
              });

              // Use fallback for city if locality wasn't found
              if (!address.city) {
                address.city = neighborhood || sublocality;
              }

              // Combine street number and route
              if (streetNumber && route) {
                address.street = `${streetNumber} ${route}`;
              } else if (route) {
                address.street = route;
              } else if (place.formatted_address) {
                // Fallback to formatted address if street components not available
                address.street = place.formatted_address.split(',')[0].trim();
              }

              // Call the autofill handler to update all form fields
              handleAddressAutofill(address);
            }
          });
        } catch (error) {
          console.error('Error initializing autocomplete:', error);
        }
      }
    };

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      // Try to initialize immediately if Google Maps is already loaded
      if (window.google && window.google.maps && window.google.maps.places) {
        initializeAutocomplete();
      } else {
        // Wait for Google Maps to load
        const checkGoogleMaps = setInterval(() => {
          if (window.google && window.google.maps && window.google.maps.places) {
            clearInterval(checkGoogleMaps);
            initializeAutocomplete();
          }
        }, 100);

        // Store interval ID for cleanup
        return () => {
          clearInterval(checkGoogleMaps);
        };
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Disable next button if required fields are empty or have errors
    const requiredFields = ['address', 'city', 'state', 'postalCode', 'country'];
    const isComplete = requiredFields.every(field => {
      const value = formData[field as keyof FormData];
      return value &&
             String(value).trim().length > 0 &&
             !fieldErrors[field];
    });
    setNextDisabled(!isComplete);
  }, [formData, fieldErrors]);

  return (
    <div className="space-y-4">
      <h2 className="text-3xl font-bold mb-4">Where is your workspace located?</h2>
      <hr className="border-t border-gray-500 my-4 w-full shadow-gray-400" />

      {/* Street Address */}
      <div className="p-4 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-3 mb-3">
          <MdLocationOn className="h-6 w-6 text-gray-500" />
          <h3 className="text-lg font-medium">Street Address</h3>
        </div>
        <input
          ref={addressInputRef}
          type="text"
          defaultValue={formData.address || ''}
          onBlur={(e) => {
            // Sync with formData when user manually types (not via autocomplete)
            if (e.target.value !== formData.address) {
              handleInputChange('address', e.target.value);
            }
          }}
          className={`w-full p-2 border rounded ${fieldErrors.address ? 'border-red-500' : ''}`}
          placeholder="Start typing address..."
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          Start typing to see address suggestions from Google Maps
        </p>
        {fieldErrors.street && <p className="text-red-500 text-sm mt-1">{fieldErrors.street}</p>}
      </div>

      {/* Apt/Unit Number (optional) */}
      <div className="p-4 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-3 mb-3">
          <MdApartment className="h-6 w-6 text-gray-500" /> 
          <h3 className="text-lg font-medium">Apt/Unit (Optional)</h3> 
        </div>
        <input
          type="text"
          value={formData.aptUnit || ''} 
          onChange={(e) => handleInputChange('aptUnit', e.target.value)} 
          className={`w-full p-2 border rounded ${fieldErrors.aptUnit ? 'border-red-500' : ''}`}
          placeholder="Apt 4B" 
        />
        {fieldErrors.aptUnit && <p className="text-red-500 text-sm mt-1">{fieldErrors.aptUnit}</p>}
      </div>
      
      {/* Floor (optional) */}
      <div className="p-4 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-3 mb-3">
          <MdApartment className="h-6 w-6 text-gray-500" /> 
          <h3 className="text-lg font-medium">Floor (Optional)</h3> 
        </div>
        <input
          type="text"
          value={formData.floor || ''} 
          onChange={(e) => handleInputChange('floor', e.target.value)} 
          className={`w-full p-2 border rounded ${fieldErrors.floor ? 'border-red-500' : ''}`}
          placeholder="5th Floor" 
        />
        {fieldErrors.floor && <p className="text-red-500 text-sm mt-1">{fieldErrors.floor}</p>}
      </div>
      
      {/* City */}
      <div className="p-4 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-3 mb-3">
          <MdHome className="h-6 w-6 text-gray-500" /> 
          <h3 className="text-lg font-medium">City</h3> 
        </div>
        <input
          type="text"
          value={formData.city || ''} 
          onChange={(e) => handleInputChange('city', e.target.value)} 
          className={`w-full p-2 border rounded ${fieldErrors.city ? 'border-red-500' : ''}`}
          placeholder="New York" 
          required
        />
        {fieldErrors.city && <p className="text-red-500 text-sm mt-1">{fieldErrors.city}</p>}
      </div>

      {/* State/Province */}
      <div className="p-4 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-3 mb-3">
          <MdMap className="h-6 w-6 text-gray-500" /> 
          <h3 className="text-lg font-medium">State/Province</h3> 
        </div>
        <input
          type="text"
          value={formData.state || ''} 
          onChange={(e) => handleInputChange('state', e.target.value)} 
          className={`w-full p-2 border rounded ${fieldErrors.state ? 'border-red-500' : ''}`}
          placeholder="NY" 
          required
        />
        {fieldErrors.state && <p className="text-red-500 text-sm mt-1">{fieldErrors.state}</p>}
      </div>

      {/* Postal/Zip Code */}
      <div className="p-4 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-3 mb-3">
          <MdMap className="h-6 w-6 text-gray-500" /> 
          <h3 className="text-lg font-medium">Postal/Zip Code</h3> 
        </div>
        <input
          type="text"
          value={formData.postalCode || ''} 
          onChange={(e) => handleInputChange('postalCode', e.target.value)} 
          className={`w-full p-2 border rounded ${fieldErrors.postalCode ? 'border-red-500' : ''}`}
          placeholder="10001" 
          required
        />
        {fieldErrors.postalCode && <p className="text-red-500 text-sm mt-1">{fieldErrors.postalCode}</p>}
      </div>

      {/* Country */}
      <div className="p-4 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-3 mb-3">
          <MdPublic className="h-6 w-6 text-gray-500" /> 
          <h3 className="text-lg font-medium">Country</h3> 
        </div>
        <input
          type="text"
          value={formData.country || ''} 
          onChange={(e) => handleInputChange('country', e.target.value)} 
          className={`w-full p-2 border rounded ${fieldErrors.country ? 'border-red-500' : ''}`}
          placeholder="United States" 
          required
        />
        {fieldErrors.country && <p className="text-red-500 text-sm mt-1">{fieldErrors.country}</p>}
      </div>
    </div>
  );
}